import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Plus, X, Check, ListTodo, ChevronDown, Users, User as UserIcon, Share2, Bell, BellOff, LayoutGrid, List, Trophy, Mic, MicOff, Loader2, Sparkles, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../AuthProvider';
import {
  notifSupported,
  notifPermission,
  notifEnabled,
  requestNotifPermission,
  disableNotifications,
  diffAndNotifyTeamTodos,
} from '../../utils/teamNotifications';

/**
 * HistoryTodoList — provider scratchpad / to-do for charting work that doesn't
 * have a history row yet.
 *
 * Built for the workflow Lori described Feb 18, 2026:
 *   "Yesterday I never got around to dictating the meds for one of my
 *    colonels — and I had to do a sleep profile for him too. Best ideas come
 *    in the morning. I want a little free-hand to-do list right here in
 *    History... and I want the OPTION to share it with my team so medics
 *    can PROPOSE items for me (like a sleep profile) even though they can't
 *    submit them themselves."
 *
 * Two scopes:
 *   - PERSONAL: localStorage-only, never leaves device. Default.
 *   - TEAM:     server-backed via /api/teams/{team_id}/todos. Any team member
 *               can add / mark done / delete. Items show the creator's name
 *               + an optional assignee.
 *
 * Provider can also enable "Auto-share new items to team" in the header so
 * personal scratchpad adds also get pushed to the active team.
 *
 * Surfaced in BOTH HistorySlidePanel and the inline HistoryPanel via this
 * single shared component (single source of truth, no drift).
 */

const API = window.__WR_API_BASE__;
const STORAGE_KEY = 'writeright_todo_list_v1';
const SCOPE_KEY = 'writeright_todo_scope';                  // 'personal' | 'team'
const AUTOSHARE_KEY = 'writeright_todo_autoshare';          // 'true' | 'false'

// Feb 27, 2026 — relative-time formatter for the "shared X ago" pill on
// auto-mirrored personal items. Keeps things human-readable without
// pulling in date-fns.
const formatRelativeTime = (ts) => {
  if (!ts) return null;
  const sec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
};
const ACTIVE_TEAM_KEY = 'writeright_todo_active_team_id';   // string

// Feb 28, 2026 — provider report: "sticky notes are saving, so it's not
// a running list you can go back to". Root cause: this loader used to
// SILENTLY delete done items older than 14 days on every page load. No
// user consent, no warning — items you marked done two weeks ago just
// vanished. We removed the silent purge. Done items now persist
// indefinitely, forming the running history the provider expects. The
// explicit "Clear Done" button is still available for intentional
// cleanup, but auto-purge is gone.
const loadPersonal = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch (_) { return []; }
};
const savePersonal = (items) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch (_) { /* quota */ }
};

const formatTimestamp = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.getFullYear() === yesterday.getFullYear() && d.getMonth() === yesterday.getMonth() && d.getDate() === yesterday.getDate();
  if (sameDay) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (isYesterday) return 'Yesterday';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const HistoryTodoList = ({ variant = 'inline' }) => {
  // Feb 28, 2026 — server-backed personal sticky notes for authed users
  // (architecture decision in chat log Feb 28, 2026 "Option A"). Falls back
  // to localStorage-only behavior for guests so existing flows still work.
  const { isAuthenticated, isGuest } = useAuth();
  const personalServerSyncEnabled = isAuthenticated && !isGuest;
  // Track whether the initial server hydration has completed — until it
  // does, we don't write back to the server (would risk pushing stale
  // localStorage state on top of fresher server state). And we don't
  // overwrite localStorage with empty server response if the user just
  // opened the app on a different device.
  // Feb 28, 2026 — initialized as TRUE only when sync is disabled (guest
  // mode) so the server helpers no-op cleanly. When the user authenticates
  // later in this same session (AuthProvider.checkAuth resolves after the
  // first render), the effect below flips these back to FALSE so the
  // hydration GET runs and the helpers gate on it. Without that reset, an
  // unauth→auth transition mid-session would let server CRUD fire BEFORE
  // we've pulled the canonical server list, risking stale-overwrite.
  const [personalHydrated, setPersonalHydrated] = useState(!personalServerSyncEnabled);
  const personalHydratedRef = useRef(!personalServerSyncEnabled);

  // Reset hydration gate whenever the auth status changes (e.g., late login).
  useEffect(() => {
    setPersonalHydrated(!personalServerSyncEnabled);
    personalHydratedRef.current = !personalServerSyncEnabled;
  }, [personalServerSyncEnabled]);

  const [scope, setScope] = useState(() => (localStorage.getItem(SCOPE_KEY) === 'team' ? 'team' : 'personal'));
  // Feb 22, 2026 — Default auto-share to ON when the provider has a team.
  // The provider explicitly said "my to-do list not streaming to team view
  // so they can pick up a few of the tedious tasks that break my momentum
  // closing notes." The previous default-off behavior buried the toggle in
  // a controls row and most providers never enabled it. Now: if the user
  // has never set a preference, autoShare starts ON. Once they save any
  // explicit choice it persists. Migration is one-way safe — if the key
  // doesn't exist, it's the first run with this build.
  const [autoShare, setAutoShare] = useState(() => {
    const stored = localStorage.getItem(AUTOSHARE_KEY);
    if (stored === null) return true; // default ON for new users
    return stored === 'true';
  });
  const [activeTeamId, setActiveTeamId] = useState(() => localStorage.getItem(ACTIVE_TEAM_KEY) || '');
  const [availableTeams, setAvailableTeams] = useState([]);
  const [personalItems, setPersonalItems] = useState(() => loadPersonal());
  const [teamItems, setTeamItems] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [draft, setDraft] = useState('');
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('writeright_todo_collapsed') === 'true');
  // Feb 18, 2026 — Board mode vs List mode toggle. Board = mini-Trello with
  // two columns (Pending | Done) so the right column gives equal visual
  // weight to what got finished, not just what's left. Inspired by a
  // conversation about "asymmetry of leftover-task lists feeding negative
  // self-talk" — defining success and ACKNOWLEDGING how close we got to it.
  const [view, setView] = useState(() => localStorage.getItem('writeright_todo_view') || 'list');
  useEffect(() => { localStorage.setItem('writeright_todo_view', view); }, [view]);
  // Lifetime "done" counter so emptying the board (Clear N done) doesn't
  // erase the win — total kept in localStorage forever.
  const [lifetimeDone, setLifetimeDone] = useState(() => {
    const stored = parseInt(localStorage.getItem('writeright_todo_lifetime_done') || '0', 10);
    return isNaN(stored) ? 0 : stored;
  });
  // Anytime we transition an item from pending → done, bump lifetimeDone.
  const lifetimeBumpRef = useRef(false);
  const inputRef = useRef(null);

  // Feb 27, 2026 — Tick every 30s so the "shared X ago" relative-time pill
  // on auto-mirrored personal items stays current without re-rendering on
  // every keystroke. Cheap one-state-update-per-30s, only triggers a
  // re-render of the widget. We don't need to track the value — the
  // counter increment is enough to invalidate memos that depend on Date.now().
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => (n + 1) % 1000000), 30000);
    return () => clearInterval(id);
  }, []);

  // ─── Personal sticky-note server sync helpers (Feb 28, 2026) ─────────────
  // All four are fire-and-forget. The local state is already optimistically
  // updated by the caller; these just push the same change to the server.
  // Failures log a console.warn but do NOT roll back the UI (the next
  // hydration on page-load will reconcile). This keeps the snappy local-
  // first feel of sticky notes while still giving cross-device persistence.
  //
  // No-op when personalServerSyncEnabled is false (guest mode / unauthed).
  //
  // ORDERING NOTE (Feb 28, 2026 — TDZ fix): These helpers MUST be declared
  // ABOVE any `useCallback` that lists them in its dependency array
  // (acceptBrainDumpItems, addItem, logDoneItem, toggleDone, deleteItem,
  // clearDone). React evaluates dep arrays during render top-to-bottom;
  // a `const` referenced before its declaration line throws "Cannot access
  // X before initialization" (the user-reported "ne" error on History tab).
  const personalServerCreate = useCallback(async (item) => {
    if (!personalServerSyncEnabled || !personalHydratedRef.current) return;
    try {
      await axios.post(
        `${API}/api/me/todos`,
        { text: item.text },
        { withCredentials: true }
      ).then((res) => {
        const serverItem = res.data?.item;
        if (serverItem?.id && serverItem.id !== item.id) {
          setPersonalItems((prev) =>
            prev.map((p) => (p.id === item.id ? { ...p, id: serverItem.id } : p))
          );
        }
      });
      window.dispatchEvent(new CustomEvent('personal-todos-changed'));
    } catch (err) {
      console.warn('Sticky-note create sync failed (will retry on next reload):', err?.message);
    }
  }, [personalServerSyncEnabled]);

  const personalServerUpdate = useCallback(async (itemId, patch) => {
    if (!personalServerSyncEnabled || !personalHydratedRef.current) return;
    try {
      await axios.patch(
        `${API}/api/me/todos/${itemId}`,
        patch,
        { withCredentials: true }
      );
      window.dispatchEvent(new CustomEvent('personal-todos-changed'));
    } catch (err) {
      console.warn('Sticky-note update sync failed (will retry on next reload):', err?.message);
    }
  }, [personalServerSyncEnabled]);

  const personalServerDelete = useCallback(async (itemId) => {
    if (!personalServerSyncEnabled || !personalHydratedRef.current) return;
    try {
      await axios.delete(
        `${API}/api/me/todos/${itemId}`,
        { withCredentials: true }
      );
      window.dispatchEvent(new CustomEvent('personal-todos-changed'));
    } catch (err) {
      console.warn('Sticky-note delete sync failed (will retry on next reload):', err?.message);
    }
  }, [personalServerSyncEnabled]);

  const personalServerBulkClearDone = useCallback(async () => {
    if (!personalServerSyncEnabled || !personalHydratedRef.current) return;
    try {
      await axios.post(
        `${API}/api/me/todos/bulk-clear-done`,
        {},
        { withCredentials: true }
      );
      window.dispatchEvent(new CustomEvent('personal-todos-changed'));
    } catch (err) {
      console.warn('Sticky-note bulk-clear sync failed (will retry on next reload):', err?.message);
    }
  }, [personalServerSyncEnabled]);

  // ─── Voice Brain-Dump (Feb 22, 2026) ──────────────────────────────────────
  // For driving-home / decompressing workflow: hit the mic, ramble about
  // everything you forgot to chart, get a clean list of action items back.
  // Uses MediaRecorder → /api/transcribe (Deepgram) → /api/todos/brain-dump
  // (LLM extracts structured tasks, strips venting/PII).
  const [bdRecording, setBdRecording] = useState(false);     // mic actively capturing
  const [bdProcessing, setBdProcessing] = useState(false);   // transcribe + extract in flight
  const [bdProposed, setBdProposed] = useState([]);          // [{text, priority, accepted}]
  const [bdShowReview, setBdShowReview] = useState(false);   // review modal open
  const [bdElapsed, setBdElapsed] = useState(0);             // recording elapsed seconds
  const [bdError, setBdError] = useState('');
  const bdRecorderRef = useRef(null);
  const bdChunksRef = useRef([]);
  const bdTimerRef = useRef(null);
  const bdStartedAtRef = useRef(0);

  // Auto-stop recording at 5 min to keep transcripts focused and prevent
  // accidental never-ending recordings (e.g., phone left in pocket).
  const BD_MAX_SECONDS = 300;

  const stopBrainDumpRecording = useCallback(() => {
    if (bdTimerRef.current) { clearInterval(bdTimerRef.current); bdTimerRef.current = null; }
    const rec = bdRecorderRef.current;
    if (rec && rec.state !== 'inactive') {
      try { rec.stop(); } catch (_) { /* already stopped */ }
    }
  }, []);

  const startBrainDumpRecording = useCallback(async () => {
    setBdError('');
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setBdError('Voice capture not supported in this browser');
      toast.error('Voice capture not supported in this browser');
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      setBdError('Microphone permission denied');
      toast.error('Microphone permission denied');
      return;
    }
    // Prefer webm/opus for Deepgram compatibility; let the browser pick if not supported.
    let mime = 'audio/webm;codecs=opus';
    if (!MediaRecorder.isTypeSupported(mime)) {
      mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
    }
    const recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    bdRecorderRef.current = recorder;
    bdChunksRef.current = [];
    recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) bdChunksRef.current.push(e.data); };
    recorder.onstop = async () => {
      // stop all tracks (release mic light)
      try { stream.getTracks().forEach(t => t.stop()); } catch (_) {}
      setBdRecording(false);
      const elapsed = Math.floor((Date.now() - bdStartedAtRef.current) / 1000);
      setBdElapsed(elapsed);
      if (bdChunksRef.current.length === 0 || elapsed < 1) {
        toast.info('Recording was too short — try again');
        return;
      }
      const blob = new Blob(bdChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
      bdChunksRef.current = [];
      setBdProcessing(true);
      try {
        // 1) Transcribe with Deepgram
        const fd = new FormData();
        const ext = (recorder.mimeType || 'audio/webm').includes('webm') ? 'webm' : 'audio';
        fd.append('file', blob, `brain-dump.${ext}`);
        const trResp = await axios.post(`${API}/api/transcribe`, fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 90000,
        });
        const transcript = (trResp.data?.text || '').trim();
        if (!transcript) {
          toast.error('No speech detected — try again, a bit closer to the mic');
          setBdProcessing(false);
          return;
        }
        // 2) Extract structured tasks via LLM
        const exResp = await axios.post(`${API}/api/todos/brain-dump`,
          { transcript },
          { withCredentials: true, timeout: 60000 }
        );
        const items = (exResp.data?.items || []).map((it, idx) => ({
          ...it,
          tmpId: `bd-${Date.now()}-${idx}`,
          accepted: true,
        }));
        if (items.length === 0) {
          toast.info('Nothing actionable found in that dump. (That\'s ok — sometimes you just needed to vent.)');
          setBdProcessing(false);
          return;
        }
        setBdProposed(items);
        setBdShowReview(true);
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || 'Brain-dump failed';
        setBdError(String(msg));
        toast.error(`Brain-dump failed: ${msg}`);
      } finally {
        setBdProcessing(false);
      }
    };

    bdStartedAtRef.current = Date.now();
    setBdElapsed(0);
    setBdRecording(true);
    recorder.start();
    bdTimerRef.current = setInterval(() => {
      const s = Math.floor((Date.now() - bdStartedAtRef.current) / 1000);
      setBdElapsed(s);
      if (s >= BD_MAX_SECONDS) {
        stopBrainDumpRecording();
      }
    }, 250);
  }, [stopBrainDumpRecording]);

  // Cancel review modal without inserting anything
  const cancelBrainDumpReview = useCallback(() => {
    setBdShowReview(false);
    setBdProposed([]);
  }, []);

  // Toggle whether a proposed item will be inserted
  const toggleBdItem = useCallback((tmpId) => {
    setBdProposed(prev => prev.map(it => it.tmpId === tmpId ? { ...it, accepted: !it.accepted } : it));
  }, []);

  // Edit text of a proposed item inline (catches LLM phrasing slips)
  const editBdItem = useCallback((tmpId, newText) => {
    setBdProposed(prev => prev.map(it => it.tmpId === tmpId ? { ...it, text: newText } : it));
  }, []);

  // Insert accepted items into the active scope (personal or team)
  const acceptBrainDumpItems = useCallback(async () => {
    const accepted = bdProposed.filter(i => i.accepted && i.text.trim());
    if (accepted.length === 0) {
      cancelBrainDumpReview();
      return;
    }
    if (scope === 'team') {
      if (!activeTeamId) { toast.error('No team selected'); return; }
      try {
        const results = await Promise.all(
          accepted.map(it =>
            axios.post(`${API}/api/teams/${activeTeamId}/todos`, { text: it.text.trim() }, { withCredentials: true })
          )
        );
        const newItems = results.map(r => r.data?.item).filter(Boolean);
        setTeamItems(prev => [...newItems, ...prev]);
        toast.success(`Added ${newItems.length} item${newItems.length === 1 ? '' : 's'} to team to-do`);
      } catch (e) {
        toast.error('Some items failed to save to team');
      }
    } else {
      const now = Date.now();
      const newItems = accepted.map((it, idx) => ({
        id: `todo-${now}-${idx}-${Math.random().toString(36).slice(2, 6)}`,
        text: it.text.trim(),
        priority: it.priority,
        done: false,
        createdAt: now + idx, // preserve order
        updatedAt: now,
        doneAt: null,
      }));
      setPersonalItems(prev => [...newItems, ...prev]);
      // Push each to the server. Each call is independent — if one fails
      // the rest still go through. The id reconciliation in
      // personalServerCreate handles the local-uuid → server-id swap.
      newItems.forEach(it => personalServerCreate(it));
      toast.success(`Added ${newItems.length} item${newItems.length === 1 ? '' : 's'} from brain-dump`);
      // Auto-share each to team if enabled. Stamp sharedAt + sharedToTeamId
      // on success so the list shows the "shared X ago" pill (Feb 27, 2026).
      if (autoShare && activeTeamId) {
        const teamIdAtShare = activeTeamId;
        newItems.forEach(it => {
          axios.post(`${API}/api/teams/${activeTeamId}/todos`, { text: it.text }, { withCredentials: true })
            .then(() => {
              setPersonalItems(prev => prev.map(p => p.id === it.id
                ? { ...p, sharedAt: Date.now(), sharedToTeamId: teamIdAtShare }
                : p
              ));
            })
            .catch(() => { /* best-effort */ });
        });
      }
    }
    setBdShowReview(false);
    setBdProposed([]);
  }, [bdProposed, scope, activeTeamId, autoShare, cancelBrainDumpReview, personalServerCreate]);

  // Cleanup on unmount — stop mic if still recording
  useEffect(() => {
    return () => {
      if (bdTimerRef.current) clearInterval(bdTimerRef.current);
      const rec = bdRecorderRef.current;
      if (rec && rec.state !== 'inactive') {
        try { rec.stop(); } catch (_) {}
      }
    };
  }, []);

  // ─── Persistence of UI prefs ─────────────────────────────────────────────
  // Personal items: always mirror to localStorage as a cache + offline
  // fallback. When server-sync is enabled, the server is the source of
  // truth (see the hydration effect below).
  useEffect(() => { savePersonal(personalItems); }, [personalItems]);

  // Feb 28, 2026 — server hydration of personal sticky notes.
  // Flow on first mount (authed users only):
  //   1. Read localStorage to grab anything the user typed BEFORE this
  //      feature shipped (or while offline).
  //   2. POST those to /api/me/todos/migrate (idempotent, capped, dedupes
  //      by id) so nothing is lost.
  //   3. GET /api/me/todos to fetch the canonical server list.
  //   4. setPersonalItems(server list) — the source of truth now.
  //   5. Mark personalHydratedRef so subsequent local mutations push back.
  // Guests skip this entire flow and stay on localStorage.
  useEffect(() => {
    if (!personalServerSyncEnabled) return;
    let cancelled = false;
    (async () => {
      try {
        // Step 1+2: ship local cache up (one-time per session — server
        // dedupes by id so re-runs are safe).
        const localCache = loadPersonal();
        if (Array.isArray(localCache) && localCache.length > 0) {
          try {
            await axios.post(
              `${API}/api/me/todos/migrate`,
              { items: localCache },
              { withCredentials: true }
            );
          } catch (_e) {
            // Migration failure is non-fatal — we still try to fetch.
          }
        }
        // Step 3: pull canonical list.
        const res = await axios.get(
          `${API}/api/me/todos?show_done=true`,
          { withCredentials: true }
        );
        if (cancelled) return;
        const serverItems = (res.data?.items || []).map((s) => ({
          id: s.id,
          text: s.text,
          done: !!s.done,
          createdAt: s.created_at ? new Date(s.created_at).getTime() : Date.now(),
          updatedAt: s.updated_at ? new Date(s.updated_at).getTime() : Date.now(),
          doneAt: s.done_at ? new Date(s.done_at).getTime() : null,
        }));
        // Sort newest first (active first, then done).
        serverItems.sort((a, b) => {
          if (a.done !== b.done) return a.done ? 1 : -1;
          return (b.updatedAt || 0) - (a.updatedAt || 0);
        });
        setPersonalItems(serverItems);
      } catch (err) {
        // Network down / 5xx — keep using local cache silently. The user
        // will see their existing items and any new ones they add will be
        // saved locally. The next successful hydration will reconcile.
        console.warn('Personal sticky-notes server sync unavailable; using local cache.', err?.message);
      } finally {
        if (!cancelled) {
          setPersonalHydrated(true);
          personalHydratedRef.current = true;
        }
      }
    })();
    return () => { cancelled = true; };
  }, [personalServerSyncEnabled]);

  useEffect(() => { localStorage.setItem('writeright_todo_collapsed', String(collapsed)); }, [collapsed]);
  useEffect(() => { localStorage.setItem(SCOPE_KEY, scope); }, [scope]);
  useEffect(() => { localStorage.setItem(AUTOSHARE_KEY, String(autoShare)); }, [autoShare]);
  useEffect(() => {
    if (activeTeamId) localStorage.setItem(ACTIVE_TEAM_KEY, activeTeamId);
  }, [activeTeamId]);

  // ─── Load available teams once on mount (best-effort; ignore failures) ──
  useEffect(() => {
    axios.get(`${API}/api/teams`, { withCredentials: true }).then(res => {
      const teams = res.data?.teams || [];
      setAvailableTeams(teams);
      if (!activeTeamId && teams.length > 0) {
        setActiveTeamId(teams[0].id);
      }
    }).catch(() => { /* guest mode or no teams — just leave Team scope disabled */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Load team todos when in team scope ──────────────────────────────────
  const reloadTeamTodos = useCallback(async () => {
    if (!activeTeamId) return;
    setLoadingTeam(true);
    try {
      const res = await axios.get(`${API}/api/teams/${activeTeamId}/todos`, { withCredentials: true });
      const items = res.data?.items || [];
      setTeamItems(items);
      // Feb 18, 2026 — Diff against last-seen to fire local notifications
      // for newly-added team to-dos. Skips the first poll after enabling.
      try {
        const me = localStorage.getItem('writeright_user_id') || '';
        diffAndNotifyTeamTodos(activeTeamId, items, me);
      } catch (_) { /* notifications are best-effort */ }
    } catch (e) {
      console.warn('team todos load failed', e);
    } finally {
      setLoadingTeam(false);
    }
  }, [activeTeamId]);
  useEffect(() => {
    if (scope === 'team' && activeTeamId) {
      reloadTeamTodos();
    }
  }, [scope, activeTeamId, reloadTeamTodos]);

  // Feb 18, 2026 — Background polling for team to-do activity. While
  // notifications are enabled AND we have an active team, hit the endpoint
  // every 30s so new items from teammates surface as desktop notifications
  // (and the inline list refreshes). Suspended while the tab is hidden
  // to be a good Chrome citizen — Page Visibility API.
  const [notifPerm, setNotifPerm] = useState(() => notifPermission());
  const notifOn = notifPerm === 'granted' && notifEnabled();
  useEffect(() => {
    if (!activeTeamId || !notifOn) return;
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      if (cancelled) return;
      if (document.visibilityState !== 'hidden') {
        try {
          const res = await axios.get(`${API}/api/teams/${activeTeamId}/todos`, { withCredentials: true });
          const items = res.data?.items || [];
          if (!cancelled) {
            // If user is viewing team scope, refresh the visible list; otherwise just diff for notifs.
            setTeamItems(prev => (scope === 'team' ? items : prev));
            const me = localStorage.getItem('writeright_user_id') || '';
            diffAndNotifyTeamTodos(activeTeamId, items, me);
          }
        } catch { /* silent */ }
      }
      timer = setTimeout(tick, 30000);  // poll every 30s
    };
    timer = setTimeout(tick, 30000);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [activeTeamId, notifOn, scope]);

  const handleEnableNotifs = useCallback(async () => {
    if (!notifSupported()) {
      toast.error('Browser notifications not supported in this environment');
      return;
    }
    const result = await requestNotifPermission();
    setNotifPerm(notifPermission());
    if (result === 'granted') {
      toast.success('Desktop notifications enabled for team activity');
    } else if (result === 'denied') {
      toast.error('Notifications were blocked. Allow them in your browser settings to re-enable.');
    } else {
      toast.info('Notification permission not granted');
    }
  }, []);

  const handleDisableNotifs = useCallback(() => {
    disableNotifications();
    setNotifPerm(notifPermission());
    toast.success('Desktop notifications paused (browser permission unchanged)');
  }, []);

  // ─── Item mutations ──────────────────────────────────────────────────────
  const addItem = useCallback(async () => {
    const text = draft.trim();
    if (!text) return;
    const now = Date.now();
    if (scope === 'team') {
      if (!activeTeamId) { toast.error('No team selected'); return; }
      try {
        const res = await axios.post(`${API}/api/teams/${activeTeamId}/todos`, { text }, { withCredentials: true });
        const item = res.data?.item;
        if (item) setTeamItems(prev => [item, ...prev]);
        setDraft('');
        toast.success('Added to team to-do');
      } catch (e) {
        toast.error('Failed to add team to-do');
        return;
      }
    } else {
      // Personal add
      const item = {
        id: `todo-${now}-${Math.random().toString(36).slice(2, 8)}`,
        text, done: false, createdAt: now, updatedAt: now, doneAt: null,
      };
      setPersonalItems(prev => [item, ...prev]);
      personalServerCreate(item);
      setDraft('');
      // Feb 27, 2026 — on successful share, stamp the personal item with
      // sharedAt + sharedToTeamId so the list can render a tiny "shared 2
      // min ago" confirmation. On failure, leave it unstamped so the user
      // sees the "Local only — share failed" cue.
      if (autoShare && activeTeamId) {
        const teamIdAtShare = activeTeamId;
        axios.post(`${API}/api/teams/${activeTeamId}/todos`, { text }, { withCredentials: true })
          .then(() => {
            setPersonalItems(prev => prev.map(p => p.id === item.id
              ? { ...p, sharedAt: Date.now(), sharedToTeamId: teamIdAtShare }
              : p
            ));
            toast.success('Added to your list + shared to team');
          })
          .catch(() => toast.warning('Saved locally, but team share failed'));
      }
    }
    if (inputRef.current) inputRef.current.focus();
  }, [draft, scope, activeTeamId, autoShare, personalServerCreate]);

  // Log a completed task in one shot — for wins WriteRight can't natively
  // capture (e.g., "Submitted 12 notes in Genesis", "Closed Feb backlog",
  // "Co-signed COL Smith's profile"). Creates the item already DONE so it
  // lands in the trophy column and bumps the lifetime counter immediately.
  // No drafts to open, no two-click pending→done dance.
  const logDoneItem = useCallback(async () => {
    const text = draft.trim();
    if (!text) return;
    const now = Date.now();
    const bumpLifetime = () => {
      setLifetimeDone(prev => {
        const next = prev + 1;
        localStorage.setItem('writeright_todo_lifetime_done', String(next));
        if (next > 0 && next % 10 === 0) {
          toast.success(`🎉 ${next} tasks completed in WriteRight. Look how much you've done.`, { duration: 4000 });
        }
        return next;
      });
    };
    if (scope === 'team') {
      if (!activeTeamId) { toast.error('No team selected'); return; }
      try {
        // Create then immediately mark done so teammates see the win + the
        // person who logged it. Two-step is unavoidable with the current API
        // but happens in the same click.
        const createRes = await axios.post(`${API}/api/teams/${activeTeamId}/todos`, { text }, { withCredentials: true });
        const created = createRes.data?.item;
        if (created) {
          const doneRes = await axios.patch(`${API}/api/teams/${activeTeamId}/todos/${created.id}`,
            { done: true }, { withCredentials: true });
          const finalItem = doneRes.data?.item || { ...created, done: true, done_at: new Date().toISOString() };
          setTeamItems(prev => [finalItem, ...prev]);
        }
        setDraft('');
        bumpLifetime();
        toast.success('Logged as done — trophy earned 🏆');
      } catch (e) {
        toast.error('Failed to log win');
        return;
      }
    } else {
      // Personal — single insert as already-done
      const item = {
        id: `todo-${now}-${Math.random().toString(36).slice(2, 8)}`,
        text, done: true, createdAt: now, updatedAt: now, doneAt: now,
        source: 'logged-done',  // tag so we can filter/dedupe later if needed
      };
      setPersonalItems(prev => [item, ...prev]);
      personalServerCreate(item);
      // After server-create completes the id may be reconciled; then
      // mark it done server-side too. Fire-and-forget — local state
      // already shows it done.
      if (personalServerSyncEnabled) {
        setTimeout(() => personalServerUpdate(item.id, { done: true }), 300);
      }
      setDraft('');
      bumpLifetime();
      toast.success('Logged as done — trophy earned 🏆');
      // Auto-share to team if enabled — share already-done so teammates can see the win
      if (autoShare && activeTeamId) {
        axios.post(`${API}/api/teams/${activeTeamId}/todos`, { text }, { withCredentials: true })
          .then(res => {
            const teamItem = res.data?.item;
            if (teamItem) {
              axios.patch(`${API}/api/teams/${activeTeamId}/todos/${teamItem.id}`, { done: true }, { withCredentials: true })
                .catch(() => { /* best-effort */ });
            }
          })
          .catch(() => { /* best-effort */ });
      }
    }
    if (inputRef.current) inputRef.current.focus();
  }, [draft, scope, activeTeamId, autoShare, personalServerCreate, personalServerUpdate, personalServerSyncEnabled]);

  const toggleDone = useCallback(async (item) => {
    // Track pending → done transition so we can bump the lifetime counter.
    const transitioningToDone = !item.done;
    const bumpLifetime = () => {
      if (transitioningToDone) {
        setLifetimeDone(prev => {
          const next = prev + 1;
          localStorage.setItem('writeright_todo_lifetime_done', String(next));
          // Soft celebration on every multiple of 10 — easy to dismiss, no modal.
          if (next > 0 && next % 10 === 0) {
            toast.success(`🎉 ${next} tasks completed in WriteRight. Look how much you've done.`, { duration: 4000 });
          }
          return next;
        });
      }
    };
    if (scope === 'team') {
      const nextDone = !item.done;
      setTeamItems(prev => prev.map(i => i.id === item.id ? { ...i, done: nextDone } : i));
      try {
        const res = await axios.patch(`${API}/api/teams/${activeTeamId}/todos/${item.id}`,
          { done: nextDone }, { withCredentials: true });
        const refreshed = res.data?.item;
        if (refreshed) setTeamItems(prev => prev.map(i => i.id === item.id ? refreshed : i));
        bumpLifetime();
      } catch (e) {
        setTeamItems(prev => prev.map(i => i.id === item.id ? { ...i, done: !nextDone } : i));
        toast.error('Failed to update');
      }
    } else {
      const nowDone = !item.done;
      setPersonalItems(prev => prev.map(i => {
        if (i.id !== item.id) return i;
        return { ...i, done: nowDone, doneAt: nowDone ? Date.now() : null, updatedAt: Date.now() };
      }));
      personalServerUpdate(item.id, { done: nowDone });
      bumpLifetime();
    }
  }, [scope, activeTeamId, personalServerUpdate]);

  const deleteItem = useCallback(async (item) => {
    if (scope === 'team') {
      const snapshot = teamItems;
      setTeamItems(prev => prev.filter(i => i.id !== item.id));
      try {
        await axios.delete(`${API}/api/teams/${activeTeamId}/todos/${item.id}`, { withCredentials: true });
      } catch (e) {
        setTeamItems(snapshot);
        toast.error('Failed to delete');
      }
    } else {
      setPersonalItems(prev => prev.filter(i => i.id !== item.id));
      personalServerDelete(item.id);
    }
  }, [scope, activeTeamId, teamItems, personalServerDelete]);

  const clearDone = useCallback(async () => {
    if (scope === 'team') {
      const doneCount = teamItems.filter(i => i.done).length;
      if (doneCount === 0) { toast.info('No completed items to clear'); return; }
      try {
        await axios.post(`${API}/api/teams/${activeTeamId}/todos/bulk-clear-done`, {}, { withCredentials: true });
        setTeamItems(prev => prev.filter(i => !i.done));
        toast.success(`Cleared ${doneCount} completed item${doneCount === 1 ? '' : 's'}`);
      } catch (e) { toast.error('Failed to clear done'); }
    } else {
      const doneCount = personalItems.filter(i => i.done).length;
      if (doneCount === 0) { toast.info('No completed items to clear'); return; }
      setPersonalItems(prev => prev.filter(i => !i.done));
      personalServerBulkClearDone();
      toast.success(`Cleared ${doneCount} completed item${doneCount === 1 ? '' : 's'}`);
    }
  }, [scope, activeTeamId, teamItems, personalItems, personalServerBulkClearDone]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addItem(); }
  };

  // Per-item "Send to team" — Feb 22, 2026. Provider's flow: rapid charting
  // with occasional tedious one-offs (correcting old profiles, chasing a
  // labs result, calling pharmacy) that BREAK momentum if she does them
  // herself but are perfect for a teammate to grab. One-click sharer.
  const shareItemToTeam = useCallback(async (item) => {
    if (!activeTeamId) {
      toast.error('No team selected — pick a team in the dropdown first');
      return;
    }
    if (item.sharedToTeamId) {
      toast.info('Already shared to team');
      return;
    }
    try {
      const res = await axios.post(`${API}/api/teams/${activeTeamId}/todos`, { text: item.text }, { withCredentials: true });
      const teamItem = res.data?.item;
      // Persist the mapping so we can show the green ✓ and prevent double-send
      setPersonalItems(prev => prev.map(p => p.id === item.id
        ? { ...p, sharedToTeamId: teamItem?.id || true, sharedAt: Date.now() }
        : p
      ));
      // Mirror into in-memory teamItems so it appears in team view immediately
      if (teamItem) setTeamItems(prev => [teamItem, ...prev]);
      toast.success('→ Shared to team. Teammates can pick it up.');
    } catch (e) {
      toast.error('Team share failed — check your connection');
    }
  }, [activeTeamId]);

  // ─── Rendering ───────────────────────────────────────────────────────────
  const items = scope === 'team' ? teamItems : personalItems;
  const itemsForRender = items.map(i => scope === 'team'
    ? {
        id: i.id, text: i.text, done: i.done,
        createdAt: i.created_at, doneAt: i.done_at,
        createdByName: i.created_by_name, doneByName: i.done_by_name,
        sharedToTeamId: null, // not applicable in team scope
      }
    : {
        id: i.id, text: i.text, done: i.done,
        createdAt: i.createdAt, doneAt: i.doneAt,
        createdByName: null, doneByName: null,
        sharedToTeamId: i.sharedToTeamId || null,
        sharedAt: i.sharedAt || null,
      }
  );
  const activeCount = itemsForRender.filter(i => !i.done).length;
  const doneCount = itemsForRender.length - activeCount;
  const isInline = variant === 'inline';
  const teamScopeAvailable = availableTeams.length > 0;
  // Feb 27, 2026 — Single always-visible status banner at the top of the
  // widget so the provider knows at a glance whether sticky notes are
  // actually being shared. Three states:
  //   • Team scope active             → "Viewing {Team Name}"
  //   • Personal + auto-share + team  → "✓ Auto-sharing to {Team Name}"
  //   • Personal + no team / off      → "Local only — not shared"
  const activeTeamName = (availableTeams.find(t => t.id === activeTeamId) || {}).name;
  let sharingStatus;
  if (scope === 'team' && activeTeamName) {
    sharingStatus = { color: 'emerald', icon: '✓', label: `Viewing ${activeTeamName} (everyone on this team sees these)` };
  } else if (autoShare && activeTeamName) {
    sharingStatus = { color: 'emerald', icon: '✓', label: `Auto-sharing to ${activeTeamName}` };
  } else if (autoShare && !activeTeamName && teamScopeAvailable) {
    sharingStatus = { color: 'amber', icon: '!', label: 'Auto-share ON but NO TEAM picked — tap "Team" tab to select one' };
  } else if (!teamScopeAvailable && personalServerSyncEnabled) {
    // Mar 1, 2026 — Signed-in user with no team: stickies ARE syncing across
    // their devices via the personal sticky-note server store. Previous label
    // ("Local only — sign in & join a team to enable sharing") wrongly implied
    // the user wasn't signed in. New label calls out cross-device sync and only
    // mentions teams as the optional next step (sharing with teammates).
    sharingStatus = { color: 'sky', icon: '✓', label: 'Synced across your devices — join a team to share with teammates' };
  } else if (!teamScopeAvailable) {
    sharingStatus = { color: 'slate', icon: '·', label: 'Local only — sign in for cross-device sync' };
  } else {
    sharingStatus = { color: 'slate', icon: '·', label: 'Local only — Auto-share is OFF' };
  }

  return (
    <div
      className={`border rounded-lg ${isInline ? 'border-amber-500/30 bg-amber-500/5 p-2' : 'border-amber-500/40 bg-amber-500/8 p-3'}`}
      data-testid="history-todo-list"
    >
      {/* ── Sharing Status Banner (Feb 27, 2026) ──────────────────────────
          Always-visible truth label so the provider can tell at a glance
          whether sticky notes are being shared with their team. Color/icon
          changes with state — emerald = sharing, amber = misconfigured,
          slate = local only. */}
      <div
        className={`mb-2 px-2.5 py-1.5 rounded-md text-[11px] font-semibold leading-tight flex items-center gap-2 border ${
          sharingStatus.color === 'emerald' ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-200'
          : sharingStatus.color === 'sky' ? 'bg-sky-500/15 border-sky-500/40 text-sky-200'
          : sharingStatus.color === 'amber' ? 'bg-amber-500/20 border-amber-500/50 text-amber-200'
          : 'bg-slate-700/40 border-slate-500/40 text-slate-300'
        }`}
        data-testid="todo-sharing-status"
        data-state={sharingStatus.color}
        title={sharingStatus.label}
      >
        <span className={`inline-flex w-4 h-4 items-center justify-center rounded-full text-[10px] font-bold ${
          sharingStatus.color === 'emerald' ? 'bg-emerald-400 text-emerald-950'
          : sharingStatus.color === 'sky' ? 'bg-sky-400 text-sky-950'
          : sharingStatus.color === 'amber' ? 'bg-amber-300 text-amber-950'
          : 'bg-slate-500 text-slate-100'
        }`}>{sharingStatus.icon}</span>
        <span className="truncate">{sharingStatus.label}</span>
      </div>

      {/* ── Brain-Dump Review Modal ─────────────────────────────────────── */}
      {bdShowReview && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={(e) => { if (e.target === e.currentTarget) cancelBrainDumpReview(); }}
          data-testid="history-todo-brain-dump-modal"
        >
          <div className="w-full max-w-lg bg-card border border-purple-500/40 rounded-xl shadow-2xl overflow-hidden">
            <div className="px-4 py-3 bg-gradient-to-r from-purple-500/20 to-amber-500/15 border-b border-purple-500/30 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-purple-300" />
              <h3 className="text-sm font-bold text-foreground flex-1">Review extracted to-dos</h3>
              <button
                type="button"
                onClick={cancelBrainDumpReview}
                className="text-muted-foreground hover:text-foreground"
                title="Cancel"
                data-testid="history-todo-brain-dump-cancel"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-4 py-2 text-[11px] text-muted-foreground/90 border-b border-border/50">
              I pulled these out of your dump. Uncheck anything that isn't a real task, edit phrasing inline, then add. Venting was filtered out automatically.
            </div>
            <ul className="max-h-[55vh] overflow-y-auto p-2 space-y-1">
              {bdProposed.map((it) => (
                <li
                  key={it.tmpId}
                  className={`flex items-start gap-2 p-2 rounded border ${
                    it.accepted ? 'border-purple-500/30 bg-purple-500/5' : 'border-border/40 bg-secondary/20 opacity-60'
                  }`}
                  data-testid={`history-todo-brain-dump-item-${it.tmpId}`}
                >
                  <button
                    type="button"
                    onClick={() => toggleBdItem(it.tmpId)}
                    className={`shrink-0 w-4 h-4 mt-0.5 rounded border flex items-center justify-center transition-colors ${
                      it.accepted
                        ? 'border-purple-500/60 bg-purple-500/30 text-purple-200'
                        : 'border-border bg-background hover:border-purple-500/60'
                    }`}
                    title={it.accepted ? 'Skip this item' : 'Include this item'}
                  >
                    {it.accepted && <Check className="w-3 h-3" />}
                  </button>
                  <input
                    type="text"
                    value={it.text}
                    onChange={(e) => editBdItem(it.tmpId, e.target.value)}
                    disabled={!it.accepted}
                    maxLength={120}
                    className="flex-1 text-xs bg-transparent border-0 focus:outline-none focus:border-b focus:border-purple-500/40 text-foreground disabled:line-through disabled:text-muted-foreground"
                  />
                  {it.priority === 'high' && (
                    <span className="shrink-0 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/40">
                      High
                    </span>
                  )}
                  {it.priority === 'low' && (
                    <span className="shrink-0 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300 border border-sky-500/30">
                      Low
                    </span>
                  )}
                </li>
              ))}
            </ul>
            <div className="px-4 py-3 border-t border-border/50 flex items-center justify-between gap-2 bg-secondary/20">
              <span className="text-[11px] text-muted-foreground">
                {bdProposed.filter(i => i.accepted).length} of {bdProposed.length} will be added to {scope === 'team' ? 'team' : 'your personal'} list
              </span>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={cancelBrainDumpReview}
                  className="text-[11px] px-3 py-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-secondary/40"
                  data-testid="history-todo-brain-dump-discard"
                >
                  Discard
                </button>
                <button
                  type="button"
                  onClick={acceptBrainDumpItems}
                  disabled={bdProposed.filter(i => i.accepted).length === 0}
                  className="text-[11px] px-3 py-1.5 rounded font-bold bg-purple-500/25 text-purple-200 border border-purple-500/40 hover:bg-purple-500/35 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                  data-testid="history-todo-brain-dump-accept"
                >
                  <Plus className="w-3 h-3" />
                  Add {bdProposed.filter(i => i.accepted).length}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setCollapsed(prev => !prev)}
          className="flex items-center gap-2 flex-1 group"
          data-testid="history-todo-toggle"
        >
          <ListTodo className={`${isInline ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-amber-400 shrink-0`} />
          <span className={`${isInline ? 'text-[11px]' : 'text-xs'} font-bold uppercase tracking-wider text-amber-300 text-left`}>
            To-Do {activeCount > 0 && <span className="ml-1 text-amber-200">({activeCount})</span>}
          </span>
          {scope === 'team' && (
            <span className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-sky-300 bg-sky-500/15 border border-sky-500/40 px-1 py-0.5 rounded">
              <Users className="w-2.5 h-2.5" /> Team
            </span>
          )}
          {scope === 'personal' && autoShare && teamScopeAvailable && (
            <span className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-emerald-300 bg-emerald-500/15 border border-emerald-500/40 px-1 py-0.5 rounded" title="New items auto-share to your team — teammates can pick them up" data-testid="history-todo-autoshare-pill-on">
              <Share2 className="w-2.5 h-2.5" /> Streaming → Team
            </span>
          )}
          {scope === 'personal' && !autoShare && teamScopeAvailable && (
            <span className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-amber-300/80 bg-amber-500/10 border border-amber-500/30 px-1 py-0.5 rounded" title="Items stay private — enable auto-share so teammates can pick up your tedious tasks" data-testid="history-todo-autoshare-pill-off">
              Private only
            </span>
          )}
          <ChevronDown className={`w-3 h-3 text-amber-400 transition-transform ml-auto ${collapsed ? '-rotate-90' : ''}`} />
        </button>
      </div>

      {!collapsed && (
        <div className={`${isInline ? 'mt-2 space-y-1.5' : 'mt-3 space-y-2'}`}>
          {/* Scope + team controls */}
          {teamScopeAvailable && (
            <div className="flex flex-wrap items-center gap-1.5 text-[10px]" data-testid="history-todo-controls">
              <div className="inline-flex rounded border border-border overflow-hidden">
                <button
                  type="button"
                  onClick={() => setScope('personal')}
                  className={`px-1.5 py-0.5 font-semibold transition-colors flex items-center gap-1 ${scope === 'personal' ? 'bg-amber-500/25 text-amber-200' : 'bg-secondary/40 text-muted-foreground hover:text-foreground'}`}
                  data-testid="history-todo-scope-personal"
                >
                  <UserIcon className="w-2.5 h-2.5" /> Personal
                </button>
                <button
                  type="button"
                  onClick={() => setScope('team')}
                  className={`px-1.5 py-0.5 font-semibold transition-colors flex items-center gap-1 ${scope === 'team' ? 'bg-sky-500/25 text-sky-200' : 'bg-secondary/40 text-muted-foreground hover:text-foreground'}`}
                  data-testid="history-todo-scope-team"
                >
                  <Users className="w-2.5 h-2.5" /> Team
                </button>
              </div>
              {availableTeams.length > 1 && (
                <select
                  value={activeTeamId}
                  onChange={(e) => setActiveTeamId(e.target.value)}
                  className="bg-input border border-border rounded px-1 py-0.5 text-[10px] text-foreground focus:outline-none focus:ring-1 focus:ring-amber-500/40"
                  data-testid="history-todo-team-select"
                >
                  {availableTeams.map(t => (<option key={t.id} value={t.id}>{t.name || 'Team'}</option>))}
                </select>
              )}
              {scope === 'personal' && (
                <label className="inline-flex items-center gap-1 cursor-pointer select-none" title="When ON, every new personal to-do is also added to your team list">
                  <input
                    type="checkbox"
                    checked={autoShare}
                    onChange={(e) => setAutoShare(e.target.checked)}
                    className="accent-emerald-500 w-3 h-3"
                    data-testid="history-todo-autoshare"
                  />
                  <span className="text-muted-foreground">Auto-share new items to team</span>
                </label>
              )}
              {/* Desktop notifications toggle — local Notification + service-worker
                  showNotification fallback. Polls team to-dos every 30s while ON. */}
              {notifSupported() && (
                <button
                  type="button"
                  onClick={notifOn ? handleDisableNotifs : handleEnableNotifs}
                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-semibold transition-colors ${
                    notifOn
                      ? 'border-emerald-500/40 bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25'
                      : notifPerm === 'denied'
                        ? 'border-destructive/40 bg-destructive/10 text-destructive/80 cursor-help'
                        : 'border-border bg-secondary/40 text-muted-foreground hover:text-foreground'
                  }`}
                  title={
                    notifOn ? 'Desktop notifications ON — click to pause' :
                    notifPerm === 'denied' ? 'Notifications blocked in browser settings' :
                    'Enable desktop notifications for new team to-dos'
                  }
                  data-testid="history-todo-notif-toggle"
                >
                  {notifOn ? <Bell className="w-2.5 h-2.5" /> : <BellOff className="w-2.5 h-2.5" />}
                  {notifOn ? 'Notif ON' : 'Notify'}
                </button>
              )}
              {doneCount > 0 && (
                <button
                  type="button"
                  onClick={clearDone}
                  className="ml-auto text-muted-foreground hover:text-foreground underline underline-offset-2"
                  data-testid="history-todo-clear-done"
                >
                  Clear {doneCount} done
                </button>
              )}
              {/* List ↔ Board toggle — mini-Trello two-column view. */}
              <div className={`inline-flex rounded border border-border overflow-hidden ${doneCount > 0 ? '' : 'ml-auto'}`} data-testid="history-todo-view-toggle">
                <button
                  type="button"
                  onClick={() => setView('list')}
                  className={`px-1.5 py-0.5 font-semibold transition-colors flex items-center gap-1 ${view === 'list' ? 'bg-amber-500/25 text-amber-200' : 'bg-secondary/40 text-muted-foreground hover:text-foreground'}`}
                  title="List view"
                  data-testid="history-todo-view-list"
                >
                  <List className="w-2.5 h-2.5" /> List
                </button>
                <button
                  type="button"
                  onClick={() => setView('board')}
                  className={`px-1.5 py-0.5 font-semibold transition-colors flex items-center gap-1 ${view === 'board' ? 'bg-amber-500/25 text-amber-200' : 'bg-secondary/40 text-muted-foreground hover:text-foreground'}`}
                  title="Board view — Pending | Done"
                  data-testid="history-todo-view-board"
                >
                  <LayoutGrid className="w-2.5 h-2.5" /> Board
                </button>
              </div>
            </div>
          )}

          {/* Quick add */}
          {scope === 'personal' && personalServerSyncEnabled && (
            <div
              className="flex items-center gap-1.5 px-2 py-1 mb-1 rounded border border-amber-500/30 bg-amber-500/5 text-[9px] text-amber-300/90"
              data-testid="phi-hint"
            >
              <AlertTriangle className="w-3 h-3 shrink-0" />
              <span>
                Stickies sync across your devices — avoid full patient names or DOB. Use rank+last
                initial (e.g., "COL D") for HIPAA safety.
              </span>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <input
              ref={inputRef}
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={
                scope === 'team'
                  ? 'Team to-do (e.g., "COL J needs sleep profile — please review")'
                  : 'e.g., "Meds for COL J (yesterday)", "Sleep profile for COL D", "FU Ortho referral"'
              }
              className={`flex-1 ${isInline ? 'h-7 text-[11px]' : 'h-8 text-xs'} bg-input border border-border focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/30 rounded px-2 text-foreground placeholder:text-muted-foreground/60 outline-none`}
              data-testid="history-todo-input"
            />
            <button
              type="button"
              onClick={addItem}
              disabled={!draft.trim()}
              className={`${isInline ? 'h-7 px-2' : 'h-8 px-3'} rounded text-[10px] font-bold transition-colors flex items-center gap-1 ${draft.trim() ? 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30' : 'bg-secondary/30 text-muted-foreground/50 cursor-not-allowed'}`}
              data-testid="history-todo-add"
              title="Add to-do (Enter)"
            >
              <Plus className="w-3 h-3" />
              Add
            </button>
            {/* Log Done — for wins WriteRight can't natively capture (Genesis
                submissions, off-app sign-offs). One click → trophy column. */}
            <button
              type="button"
              onClick={logDoneItem}
              disabled={!draft.trim()}
              className={`${isInline ? 'h-7 px-2' : 'h-8 px-3'} rounded text-[10px] font-bold transition-all flex items-center gap-1 border ${
                draft.trim()
                  ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20'
                  : 'border-border bg-secondary/30 text-muted-foreground/50 cursor-not-allowed'
              }`}
              data-testid="history-todo-log-done"
              title="Log as already DONE — straight to the trophy column (for wins WriteRight can't track: Genesis submits, co-signs, etc.)"
            >
              <Trophy className="w-3 h-3" />
              Log Done
            </button>
            {/* Voice Brain-Dump — talk freely, AI extracts structured to-dos */}
            <button
              type="button"
              onClick={bdRecording ? stopBrainDumpRecording : startBrainDumpRecording}
              disabled={bdProcessing}
              className={`${isInline ? 'h-7 px-2' : 'h-8 px-3'} rounded text-[10px] font-bold transition-all flex items-center gap-1 border ${
                bdProcessing
                  ? 'border-purple-500/40 bg-purple-500/15 text-purple-300 cursor-wait'
                  : bdRecording
                    ? 'border-red-500/60 bg-red-500/20 text-red-300 animate-pulse'
                    : 'border-purple-500/40 bg-purple-500/10 text-purple-300 hover:bg-purple-500/20'
              }`}
              data-testid="history-todo-brain-dump"
              title={
                bdProcessing
                  ? 'Processing your dump…'
                  : bdRecording
                    ? `Recording — tap to stop (${Math.floor(bdElapsed/60)}:${String(bdElapsed%60).padStart(2,'0')} / ${Math.floor(BD_MAX_SECONDS/60)}:00 max)`
                    : 'Voice Brain-Dump: ramble out loud, AI extracts the to-dos'
              }
            >
              {bdProcessing ? (
                <><Loader2 className="w-3 h-3 animate-spin" /> Thinking…</>
              ) : bdRecording ? (
                <><MicOff className="w-3 h-3" /> Stop {Math.floor(bdElapsed/60)}:{String(bdElapsed%60).padStart(2,'0')}</>
              ) : (
                <><Mic className="w-3 h-3" /> Brain-Dump</>
              )}
            </button>
          </div>

          {/* Brain-dump status line — visible only when recording/error */}
          {(bdRecording || bdError) && (
            <p
              className={`text-[10px] px-1 ${bdError ? 'text-destructive' : 'text-purple-300/80 italic'}`}
              data-testid="history-todo-brain-dump-status"
            >
              {bdError ? (
                <><AlertTriangle className="inline w-2.5 h-2.5 mr-1 -mt-0.5" />{bdError}</>
              ) : (
                <>Listening… talk freely. I'll filter the venting and keep the action items.</>
              )}
            </p>
          )}

          {loadingTeam && scope === 'team' && (
            <p className="text-[10px] text-muted-foreground/70 italic px-1 py-1">Loading team to-dos…</p>
          )}

          {itemsForRender.length === 0 && !loadingTeam ? (
            <p className={`${isInline ? 'text-[10px]' : 'text-[11px]'} text-muted-foreground/70 italic px-1 py-1`}>
              {scope === 'team'
                ? 'No team to-dos yet. Medics & nurses on your team can propose items here too.'
                : 'Jot down anything you didn\'t get to chart — meds, profiles, follow-ups. Persists across sessions.'}
            </p>
          ) : view === 'board' ? (
            // ── Mini-Trello: Pending | Done ────────────────────────────────
            // Two columns with equal visual weight so the right column tells
            // the success story, not just the leftover-task story. Bottom
            // strip shows lifetime "tasks completed" so emptying the board
            // doesn't erase the win.
            <div className="grid grid-cols-2 gap-2" data-testid="history-todo-board">
              <div className="rounded border border-amber-500/30 bg-amber-500/5 overflow-hidden">
                <div className="px-2 py-1 bg-amber-500/15 border-b border-amber-500/30 flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-amber-300">Pending</span>
                  <span className="text-[9px] font-semibold text-amber-200 bg-amber-500/20 px-1.5 py-0.5 rounded-full">{activeCount}</span>
                </div>
                <ul className="p-1 space-y-0.5 max-h-[180px] overflow-y-auto">
                  {itemsForRender.filter(i => !i.done).length === 0 ? (
                    <li className="text-[10px] text-muted-foreground/60 italic px-1.5 py-1">All caught up. ✓</li>
                  ) : itemsForRender.filter(i => !i.done).map(item => (
                    <li key={item.id} className="flex items-start gap-1.5 py-0.5 px-1 rounded hover:bg-secondary/40 group" data-testid={`history-todo-item-${item.id}`}>
                      <button
                        type="button"
                        onClick={() => toggleDone(items.find(i => i.id === item.id))}
                        className="shrink-0 w-3.5 h-3.5 mt-0.5 rounded border border-border bg-background hover:border-amber-500/60 hover:bg-amber-500/20 transition-colors"
                        title="Mark as done"
                        data-testid={`history-todo-check-${item.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <span className="block text-[11px] text-foreground break-words" title={item.text}>{item.text}</span>
                        {scope === 'team' && item.createdByName && (
                          <span className="block text-[9px] text-muted-foreground/70 truncate">added by {item.createdByName}</span>
                        )}
                        {scope === 'personal' && item.sharedToTeamId && (
                          <span className="block text-[9px] text-sky-300 truncate" data-testid={`history-todo-shared-pill-${item.id}`}>
                            → shared to team{item.sharedAt ? ` · ${formatRelativeTime(item.sharedAt)}` : ''}
                          </span>
                        )}
                      </div>
                      {/* Per-item team-share — only on personal scope items not yet shared */}
                      {scope === 'personal' && activeTeamId && !item.sharedToTeamId && (
                        <button
                          type="button"
                          onClick={() => shareItemToTeam(items.find(i => i.id === item.id))}
                          className="shrink-0 w-3.5 h-3.5 text-sky-400/60 hover:text-sky-300 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Send to team — let a teammate pick this up"
                          data-testid={`history-todo-share-${item.id}`}
                        >
                          <Share2 className="w-3 h-3" />
                        </button>
                      )}
                      {scope === 'personal' && item.sharedToTeamId && (
                        <span
                          className="shrink-0 w-3.5 h-3.5 text-sky-400/80 flex items-center justify-center"
                          title="Shared to team"
                          data-testid={`history-todo-shared-indicator-${item.id}`}
                        >
                          <Check className="w-3 h-3" />
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => deleteItem(items.find(i => i.id === item.id))}
                        className="shrink-0 w-3.5 h-3.5 text-muted-foreground/40 hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Delete"
                        data-testid={`history-todo-delete-${item.id}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded border border-emerald-500/30 bg-emerald-500/5 overflow-hidden">
                <div className="px-2 py-1 bg-emerald-500/15 border-b border-emerald-500/30 flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-300 flex items-center gap-1">
                    <Trophy className="w-2.5 h-2.5" /> Done
                  </span>
                  <span className="text-[9px] font-semibold text-emerald-200 bg-emerald-500/20 px-1.5 py-0.5 rounded-full">{doneCount}</span>
                </div>
                <ul className="p-1 space-y-0.5 max-h-[180px] overflow-y-auto">
                  {itemsForRender.filter(i => i.done).length === 0 ? (
                    <li className="text-[10px] text-muted-foreground/60 italic px-1.5 py-1">Mark something done — let's see it land here.</li>
                  ) : itemsForRender.filter(i => i.done).map(item => (
                    <li key={item.id} className="flex items-start gap-1.5 py-0.5 px-1 rounded hover:bg-secondary/40 group" data-testid={`history-todo-item-${item.id}`}>
                      <button
                        type="button"
                        onClick={() => toggleDone(items.find(i => i.id === item.id))}
                        className="shrink-0 w-3.5 h-3.5 mt-0.5 rounded border border-emerald-500/60 bg-emerald-500/20 text-emerald-400 flex items-center justify-center"
                        title="Move back to pending"
                        data-testid={`history-todo-check-${item.id}`}
                      >
                        <Check className="w-2.5 h-2.5" />
                      </button>
                      <div className="flex-1 min-w-0">
                        <span className="block text-[11px] line-through text-emerald-100/70 break-words" title={item.text}>{item.text}</span>
                        {scope === 'team' && item.doneByName && (
                          <span className="block text-[9px] text-emerald-200/70 truncate">✓ {item.doneByName}</span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => deleteItem(items.find(i => i.id === item.id))}
                        className="shrink-0 w-3.5 h-3.5 text-muted-foreground/40 hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Delete"
                        data-testid={`history-todo-delete-${item.id}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : (
            <ul className="space-y-0.5 max-h-[200px] overflow-y-auto pr-1">
              {itemsForRender.map((item) => (
                <li
                  key={item.id}
                  className={`flex items-center gap-1.5 ${isInline ? 'py-0.5 px-1' : 'py-1 px-1.5'} rounded hover:bg-secondary/40 group`}
                  data-testid={`history-todo-item-${item.id}`}
                >
                  <button
                    type="button"
                    onClick={() => toggleDone(items.find(i => i.id === item.id))}
                    className={`shrink-0 ${isInline ? 'w-3.5 h-3.5' : 'w-4 h-4'} rounded border ${item.done ? 'border-emerald-500/60 bg-emerald-500/20 text-emerald-400' : 'border-border bg-background hover:border-amber-500/60'} flex items-center justify-center transition-colors`}
                    title={item.done ? 'Mark as not done' : 'Mark as done'}
                    data-testid={`history-todo-check-${item.id}`}
                  >
                    {item.done && <Check className={isInline ? 'w-2.5 h-2.5' : 'w-3 h-3'} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <span
                      className={`block ${isInline ? 'text-[11px]' : 'text-xs'} ${item.done ? 'line-through text-muted-foreground/60' : 'text-foreground'} truncate`}
                      title={item.text}
                    >
                      {item.text}
                    </span>
                    {scope === 'team' && (item.createdByName || (item.done && item.doneByName)) && (
                      <span className="block text-[9px] text-muted-foreground/70 truncate">
                        {item.done && item.doneByName
                          ? `✓ done by ${item.doneByName}`
                          : `added by ${item.createdByName}`}
                      </span>
                    )}
                    {scope === 'personal' && item.sharedToTeamId && (
                      <span className="block text-[9px] text-sky-300 truncate">→ shared to team</span>
                    )}
                  </div>
                  <span className={`shrink-0 ${isInline ? 'text-[9px]' : 'text-[10px]'} text-muted-foreground/70 tabular-nums`}>
                    {formatTimestamp(item.done ? item.doneAt : item.createdAt)}
                  </span>
                  {/* Per-item team-share — Feb 22, 2026. Flag a tedious personal
                      task for the team to pick up. */}
                  {scope === 'personal' && activeTeamId && !item.sharedToTeamId && !item.done && (
                    <button
                      type="button"
                      onClick={() => shareItemToTeam(items.find(i => i.id === item.id))}
                      className={`shrink-0 ${isInline ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-sky-400/60 hover:text-sky-300 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center`}
                      title="Send to team — let a teammate pick this up"
                      data-testid={`history-todo-share-${item.id}`}
                    >
                      <Share2 className={isInline ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
                    </button>
                  )}
                  {scope === 'personal' && item.sharedToTeamId && (
                    <span
                      className={`shrink-0 ${isInline ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-sky-400/80 flex items-center justify-center`}
                      title="Shared to team"
                      data-testid={`history-todo-shared-indicator-${item.id}`}
                    >
                      <Check className={isInline ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => deleteItem(items.find(i => i.id === item.id))}
                    className={`shrink-0 ${isInline ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-muted-foreground/40 hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity`}
                    title="Delete"
                    data-testid={`history-todo-delete-${item.id}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Lifetime "done" counter — keeps the win visible even after you
              clear completed items. Defining success AND acknowledging how
              close you got to it. */}
          {lifetimeDone > 0 && (
            <div className="flex items-center gap-1.5 px-1.5 py-1 mt-1 rounded bg-emerald-500/8 border border-emerald-500/25" data-testid="history-todo-lifetime-done">
              <Trophy className="w-3 h-3 text-emerald-400 shrink-0" />
              <span className="text-[10px] text-emerald-300/90">
                <span className="font-bold text-emerald-200">{lifetimeDone}</span> task{lifetimeDone === 1 ? '' : 's'} completed in WriteRight.
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HistoryTodoList;
